import { ex } from "@fullcalendar/core/internal-common";
import { AccountProfile } from "src/services/account/account.types";
import { IMeeting } from "src/services/meetings/meetings.types";
import { User } from "src/services/organisation/organisation.types";

export enum AvailabilityRuleType {

  SPECIFIC_DATE = 'specific_date',
  DATE_RANGE = 'date_range',
  DAILY_RECURRING = 'daily_recurring',
  WEEKLY_RECURRING = 'weekly_recurring',
  MONTHLY_RECURRING = 'monthly_recurring',

}

export enum AvailabilityType {
  AVAILABLE = 'available',
  UNAVAILABLE = 'unavailable',
}

export enum DayOfWeek {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

export enum CalendarEventStatus {
  SCHEDULED = 'scheduled',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
}

export enum CalendarEventType {
  MEETING = 'meeting',
  TASK = 'task',
  REMINDER = 'reminder',
  EVENT_BOOKING = 'event_booking',
  OTHER = 'other',
}

export enum CalendarEventSource {
  MANUAL = 'manual',
  EXTERNAL_SYNC = 'external_sync',
  SYSTEM = 'system',
}

export enum RollingWindow {
  ONE_WEEK = 'one_week',
  TWO_WEEKS = 'two_weeks',
  THREE_WEEKS = 'three_weeks',
  ONE_MONTH = 'one_month',
  THREE_MONTHS = 'three_months',
  SIX_MONTHS = 'six_months',
  ONE_YEAR = 'one_year',
  ONGOING = 'ongoing',
}

export enum EventTypeStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DISABLED = 'disabled',
}

export enum CalendarEventAvailability {
  BUSY = 'busy',
  FREE = 'free'
}

export enum CalendarEventVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
}

export interface ICalendar {
  id: string;
  owner?: Partial<User>;
  timezone: string;
  last_synced_at: Date;
  sync_fail_reason?: string;
  metadata: Record<string, any>;
  created_at: Date;
  updated_at: Date;
}

export interface ICalendarEvent {
  id: string;
  title: string;
  description?: string;
  start_at: Date;
  end_at?: Date;
  all_day: boolean;
  location?: string;
  status: CalendarEventStatus;
  type: CalendarEventType;
  source: CalendarEventSource;
  external_url?: string;
  metadata: Record<string, any>;
  calendar: Pick<ICalendar, 'id'>;
  created_at: Date;
  updated_at: Date;
  visibility: CalendarEventVisibility;
  availability_type: CalendarEventAvailability;
  /** Raw provider free/busy status (free | workingElsewhere | tentative | busy | oof). */
  show_as?: string;
  meeting: Partial<IMeeting> | null;
  attendees?: {
    email: string;
    first_name: string;
    last_name: string;
  }[];
}

export interface CreateCalendarEventDto {
  title: string;
  description?: string;
  start_at: Date;
  end_at?: Date;
  all_day?: boolean;
  location?: string;
  type?: CalendarEventType;
  source?: CalendarEventSource;
  external_url?: string;
  metadata?: Record<string, any>;
}

export interface UpdateCalendarEventDto {
  title?: string;
  description?: string;
  start_at?: Date;
  end_at?: Date;
  all_day?: boolean;
  location?: string;
  status?: CalendarEventStatus;
  type?: CalendarEventType;
  source?: CalendarEventSource;
  external_url?: string;
  metadata?: Record<string, any>;
}


export type BookableEventType = {
  id: string;
  name: string;
  description?: string;
  duration_minutes: number;
  buffer_minutes?: number;
  slot_increment_minutes: number;
  status: EventTypeStatus;
  company?: {
    id: string;
    name: string;
  }
  link?: string; // Generated link for booking
  location?: string;
  account: AccountProfile;
  autocreate_meeting: boolean;
  create_meeting_bot: boolean; // Whether to create a meeting bot for the event

  created_at: Date;
  updated_at: Date;
}

export interface CreateBookableEventTypeDto {
  id: string;
  name: string;
  description?: string;
  duration_minutes: number;
  buffer_minutes?: number;
  autocreate_meeting?: boolean;
  create_meeting_bot?: boolean;
  slot_increment_minutes: number;
  location?: string;
  metadata?: Record<string, any>;
}

export interface UpdateBookableEventTypeDto {
  name?: string;
  description?: string;
  duration_minutes?: number;
  buffer_minutes?: number;
  slot_increment_minutes?: number;
  status?: EventTypeStatus;
  location?: string;
  autocreate_meeting?: boolean;
  create_meeting_bot?: boolean;
  metadata?: Record<string, any>;
}

export type AvailabilityRule = {

  id: string;
  type: AvailabilityType;
  rule_type: AvailabilityRuleType;
  date?: Date;
  start_date?: Date;
  end_date?: Date;
  start_time?: string;
  end_time?: string;
  days_of_week?: {
    [key in DayOfWeek]?: boolean;
  };
  days_of_month?: {
    [key: number]: boolean;
  };
  is_active: boolean;
  created_at: Date;
  updated_at: Date;
  event_type: BookableEventType;
  priority?: number; // 0: Low, 1: Medium, 2: High
  rolling_window?: RollingWindow;

}

export interface CreateAvailabilityRuleDto {

  rule_type: AvailabilityRuleType;
  type: AvailabilityType;
  date?: Date;
  start_date?: Date;
  end_date?: Date;
  start_time: string; // HH:MM format
  end_time: string; // HH:MM format
  days_of_week?: DayOfWeek[];
  days_of_month?: number[]; // 1-31 for monthly recurrence
  priority?: number;
  rolling_window?: RollingWindow;
}

export interface UpdateAvailabilityRuleDto {
  date?: Date;
  start_date?: Date;
  end_date?: Date;
  start_time?: string; // HH:MM format
  end_time?: string; // HH:MM format
  days_of_week?: DayOfWeek[];
  days_of_month?: number[]; // 1-31 for monthly recurrence
  priority?: number;
  rolling_window?: RollingWindow;
}

export interface GetAvailableSlotsDto {
  date: string; // YYYY-MM-DD format
  timezone?: string;
}

export interface GetMonthMetadataDto {
  month: number; // 1-12
  year: number; // YYYY format
  timezone?: string;
}

export type AvailabilitySlot = {
  start_time: Date;
  end_time: Date;
  start_time_local: string; // ISO string in requestor's timezone
  end_time_local: string; // ISO string in requestor's timezone,
  rule_id?: string;
}

export interface MakeAppointmentDto {

  attendee: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    country_code?: string;
    company?: string;
  };

  timeslot: {
    start_time: Date;
    end_time: Date;
  };

  notes?: string;
}

